// The public status page's reading of GET /api/status (KPR-320). Pure, so the
// page and its tests share one definition of "healthy", "degraded" and
// "something we cannot show".

export const HEALTH = Object.freeze({
	ok: 'Operational',
	degraded: 'Degraded',
	down: 'Down',
});

/** The banner's words for the platform as a whole. */
export const BANNER_TEXT = Object.freeze({
	ok: 'All systems operational',
	degraded: 'Some systems are degraded',
	down: 'keepr is down',
	unreachable: 'keepr’s API is unreachable',
});

const isHealth = value => Object.prototype.hasOwnProperty.call(HEALTH, value);

/**
 * The body as the page shows it, or null when it is not the contract's shape
 * (so the page says unreachable rather than rendering a guess). The list is
 * iterated, never indexed, and a component keeps only the four fields the
 * page reads: one added later, or a field added to one, changes nothing here.
 * The top-level `uptime` (KPR-758) is read by normalizeUptime below. A
 * component whose own status is a word we do not know is shown as degraded,
 * never as fine.
 */
export function normalizeStatus(body) {
	if (!body || typeof body !== 'object' || !isHealth(body.status) || !Array.isArray(body.components)) return null;
	const components = body.components
		.filter(component => component && typeof component === 'object' && typeof component.id === 'string' && typeof component.name === 'string')
		.map(component => ({
			id: component.id,
			name: component.name,
			version: typeof component.version === 'string' && component.version.trim() ? component.version.trim() : null,
			status: isHealth(component.status) ? component.status : 'degraded',
		}));
	return {
		status: body.status,
		checkedAt: typeof body.checkedAt === 'string' ? body.checkedAt : null,
		components,
		uptime: normalizeUptime(body.uptime),
	};
}

// ---- Uptime (KPR-758) -------------------------------------------------------
//
// GET /api/status may carry a top-level `uptime` object keyed by component id
// (the web row's `web` included): days30, days90, 90 UTC daily percentages and
// the latest incidents, measured from outside keepr by Route 53. It is
// optional, so anything that is not the contract's shape is dropped entry by
// entry, never the page.

const COMPONENT_ID = /^[a-z][a-z0-9-]{0,31}$/;
const UTC_DATE = /^\d{4}-\d{2}-\d{2}$/;
export const UPTIME_DAYS = 90;
export const UPTIME_INCIDENTS_SHOWN = 3;

const percentOrNull = value => (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100 ? value : null);

/** One entry as the page shows it, or null when it is not an entry. */
function normalizeUptimeEntry(entry) {
	if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
	const daily = (Array.isArray(entry.daily) ? entry.daily : [])
		.filter(d => d && typeof d === 'object' && typeof d.date === 'string' && UTC_DATE.test(d.date))
		.map(d => ({ date: d.date, percent: percentOrNull(d.percent) }))
		.slice(-UPTIME_DAYS);
	const incidents = (Array.isArray(entry.incidents) ? entry.incidents : [])
		.filter(i => i && typeof i === 'object' && typeof i.start === 'string' && !Number.isNaN(Date.parse(i.start))
			&& Number.isInteger(i.durationMinutes) && i.durationMinutes >= 1)
		.map(i => ({ start: i.start, durationMinutes: i.durationMinutes }))
		.sort((a, b) => Date.parse(b.start) - Date.parse(a.start));
	return { days30: percentOrNull(entry.days30), days90: percentOrNull(entry.days90), daily, incidents };
}

/** The `uptime` map, keyed by component id; {} when there is none. */
export function normalizeUptime(raw) {
	const out = {};
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
	for (const [id, entry] of Object.entries(raw)) {
		if (!COMPONENT_ID.test(id)) continue;
		const clean = normalizeUptimeEntry(entry);
		if (clean) out[id] = clean;
	}
	return out;
}

/** A day's health word for its percentage: every minute up, some down, or most of it down. */
export function uptimeLevel(percent) {
	if (percent === null || percent === undefined) return 'none';
	if (percent >= 100) return 'ok';
	if (percent >= 99) return 'degraded';
	return 'down';
}

/**
 * 99.95 -> "99.95%", 100 -> "100%". The API sends at most two decimals,
 * rounded down; truncating here too means a stray third never rounds up to 100.
 */
export function formatPercent(percent) {
	return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2, roundingMode: 'trunc' }).format(percent)}%`;
}

/** A UTC calendar day ('2026-10-03') as the reader says it, the same day in every time zone. */
export function formatUtcDay(date) {
	return new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** When an incident began, in the reader's own time zone (or `timeZone`), which it names. */
export function formatIncidentStart(iso, timeZone) {
	return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone });
}

/** 1 -> "1 minute", 75 -> "1 hour 15 minutes". */
export function formatMinutes(minutes) {
	const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
	if (minutes < 60) return plural(minutes, 'minute');
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	return rest ? `${plural(hours, 'hour')} ${plural(rest, 'minute')}` : plural(hours, 'hour');
}

/** The bar's words for a screen reader: how many days were whole, had downtime, or had no data. */
export function dailySummary(daily) {
	const count = level => daily.filter(d => uptimeLevel(d.percent) === level).length;
	const whole = count('ok');
	const some = count('degraded') + count('down');
	const none = count('none');
	const parts = [`${whole} ${whole === 1 ? 'day' : 'days'} with no downtime`];
	if (some) parts.push(`${some} ${some === 1 ? 'day' : 'days'} with some downtime`);
	if (none) parts.push(`${none} ${none === 1 ? 'day' : 'days'} not measured`);
	return `Daily uptime for the last ${daily.length} days: ${parts.join(', ')}.`;
}
